import React, { useState } from 'react';
import styled from 'styled-components';

interface SpeakerAvatarProps {
  name: string;
  color: string;
  headshotUrl?: string | null;
  size?: number;
  /** 1 = first initial (default); 2 = first + last initials. */
  initialsLength?: 1 | 2;
  /** Initials text color (default: white, for colored badges). */
  initialsColor?: string;
  /** Initials font size (default: scales with `size`). */
  initialsFontSize?: string;
  /** Initials font weight (default: semi-bold). */
  initialsFontWeight?: string;
}

function getInitials(name: string, length: 1 | 2): string {
  const parts = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (parts.length === 0) return '?';
  if (length === 1 || parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Unified speaker avatar component.
 *
 * - With headshot: circular image that scales up on hover
 * - Without headshot: circular badge with speaker initials on colored background
 * - Falls back to initial avatar on image load error
 */
const SpeakerAvatar: React.FC<SpeakerAvatarProps> = ({
  name,
  color,
  headshotUrl,
  size = 28,
  initialsLength = 1,
  initialsColor = '#fff',
  initialsFontSize,
  initialsFontWeight = 'var(--font-weight-semi-bold)',
}) => {
  const [imgError, setImgError] = useState(false);
  const showHeadshot = !!headshotUrl && !imgError;
  const initial = getInitials(name, initialsLength);

  return (
    <AvatarWrapper $size={size}>
      {showHeadshot ? (
        <HeadshotImage
          src={headshotUrl!}
          alt={name}
          onError={() => setImgError(true)}
        />
      ) : (
        <InitialAvatar
          $color={color}
          $size={size}
          $textColor={initialsColor}
          $fontSize={initialsFontSize}
          $fontWeight={initialsFontWeight}
        >
          {initial}
        </InitialAvatar>
      )}
    </AvatarWrapper>
  );
};

const AvatarWrapper = styled.div<{ $size: number }>`
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.15);
  }
`;

const HeadshotImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  display: block;
`;

const InitialAvatar = styled.div<{
  $color: string;
  $size: number;
  $textColor: string;
  $fontSize?: string;
  $fontWeight: string;
}>`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: ${props => props.$color};
  color: ${props => props.$textColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props =>
    props.$fontSize ?? `${Math.round(props.$size * 0.45)}px`};
  font-weight: ${props => props.$fontWeight};
  line-height: 1;
  user-select: none;
`;

export default SpeakerAvatar;
